/**
 * Pool action modals — Activate Bonus / Transfer Commission / Convert Cashback.
 *
 * Three separate modal components, each handling its own form logic and
 * TanStack mutation. All call the wallet pool-action API from poolActions.ts,
 * then invalidate WALLET_QUERY_KEYS.balances on success.
 *
 * Usage: render <ActivateBonusModal>, <TransferCommissionModal>, or
 * <ConvertCashbackModal> and pass open/onClose props.
 */
import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Money } from '@/components/money/Money.js';
import { WALLET_QUERY_KEYS } from '@/features/wallet/api.js';
import {
  activateBonus,
  transferCommission,
  convertCashback,
  poolIdempotencyKey,
} from '@/features/wallet/poolActions.js';
import type { PoolAction } from '@/features/wallet/poolActions.js';
import { mapApiError } from '@/lib/api/error.js';
import { ApiError } from '@/lib/api/error.js';
import { tryToMinorUnits } from '@/lib/money/index.js';
import { useDisplayMoney, useFormatMoney } from '@/components/money/MoneyProvider.js';

/**
 * The typed amount in LEDGER minor units. A ledger-currency pool is typed in the
 * currency the site shows it in (what `Max` reads), and converted back.
 */
function useTypedLedgerMinor(raw: string, currency: string | undefined): bigint | null {
  const view = useDisplayMoney();
  const other = currency == null || currency.toUpperCase() === view.ledgerCurrency ? null : currency;
  const parsed = tryToMinorUnits(raw || '0', other ?? view.currency);
  if (!parsed.ok) return null;
  return other === null ? view.toLedger(parsed.minor) : parsed.minor;
}

/**
 * A caller-held idempotency key, scoped to the FIGURES of the request.
 *
 * svc-wallet dedups a pool action on the key the caller sends and on nothing
 * else: the key it mints for a keyless caller is fresh per request, by design
 * (#141). `transfer-commission` and `convert-cashback` take their amount from
 * the caller, so a second identical request moves the money a second time —
 * nothing about the pool balance stops it the way an emptied bonus pool stops
 * `activate-bonus`.
 *
 * The vector is neither a double-click (the button is disabled while pending)
 * nor an auto-retry (`mutations: { retry: 0 }`, and the one retry `apiFetch`
 * does after a refresh cannot re-post because `authGuard` rejects before the
 * handler runs). It is the player pressing the button again after an error for
 * a request svc-wallet had in fact committed. So the key survives across
 * attempts at the same amount, and changes when the amount does.
 *
 * The key is NAMESPACED, `pool:<action>:<uuid>` via `poolIdempotencyKey`, not a
 * bare uuid (#208). A bare uuid says nothing about where the key came from, so
 * svc-wallet could not require a machine provenance on these routes without
 * refusing whatever an older bundle is still sending — and the SPA deploys
 * through Cloudflare Pages on a pipeline of its own. This side shipped first
 * and alone; the guard at `apps/wallet/src/routes/pool-actions.ts` followed in
 * #257 and still accepts a bare uuid for exactly that reason.
 *
 * `action` is a separate argument from `signature` because it belongs in the
 * KEY, while the amount does not: the key travels to a backoffice screen and to
 * the player's own transaction history, and the figures have no business there.
 * Folding it into the cache scope as well is only tidiness — each modal calls
 * this hook itself, so each holds its own ref and no two actions ever shared one.
 */
function useIdempotencyKey() {
  const ref = useRef<{ signature: string; key: ReturnType<typeof poolIdempotencyKey> } | null>(null);
  return (action: PoolAction, signature: string) => {
    const scope = `${action}:${signature}`;
    if (ref.current?.signature !== scope) {
      ref.current = { signature: scope, key: poolIdempotencyKey(action) };
    }
    return ref.current.key;
  };
}

// ── Activate Bonus ────────────────────────────────────────────────────────────

interface ActivateBonusModalProps {
  open: boolean;
  onClose: () => void;
  currency?: string; // reserved for future display use
}

export function ActivateBonusModal({ open, onClose, currency: _currency }: ActivateBonusModalProps) {
  const { t } = useTranslation('wallet');
  const qc = useQueryClient();

  const idempotencyKeyFor = useIdempotencyKey();

  const mutation = useMutation({
    mutationFn: () => activateBonus(idempotencyKeyFor('activate-bonus', '')),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('actions.success.activated'));
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 501) {
        toast.info(t('actions.comingSoon'));
      } else {
        toast.error(mapApiError(err, t));
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('activate.confirmTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-[13px] text-[var(--text-secondary)]">
          {t('activate.confirmBody')}
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </Button>
          <Button
            fullWidth
            glow
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '…' : t('actions.activate')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Transfer Commission ───────────────────────────────────────────────────────

interface TransferCommissionModalProps {
  open: boolean;
  onClose: () => void;
  maxMinor: number;
  currency?: string;
}

export function TransferCommissionModal({
  open,
  onClose,
  maxMinor,
  currency,
}: TransferCommissionModalProps) {
  const { t } = useTranslation('wallet');
  const qc = useQueryClient();
  const [raw, setRaw] = useState('');
  const idempotencyKeyFor = useIdempotencyKey();

  // Currency-aware, not `* 100`: these move real balance between pools, so on a
  // 0-decimal brand the hardcoded multiplier transferred 100x what was typed
  // (same defect as the deposit/withdraw forms, WEB-02).
  // Parsed, not thrown: this runs in a render body, and `type="number"` accepts
  // `1e3` as a valid value, so `BigInt()` threw straight out of the component
  // and took the whole SPA with it (WEB-22). Unparseable text is simply not a
  // valid amount.
  const typedMinor = useTypedLedgerMinor(raw, currency);
  const amountMinor = typedMinor === null ? 0 : Number(typedMinor);
  const isValid = typedMinor !== null && amountMinor > 0 && amountMinor <= maxMinor;

  const mutation = useMutation({
    mutationFn: () =>
      transferCommission(amountMinor, idempotencyKeyFor('transfer-commission', String(amountMinor))),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('actions.success.transferred'));
      setRaw('');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 501) {
        toast.info(t('actions.comingSoon'));
      } else {
        toast.error(mapApiError(err, t));
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('transfer.modalTitle')}</DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-[var(--text-muted)]">
          {t('pools.commission.sub')} ·{' '}
          <span className="text-[var(--warning)] font-semibold">
            <Money minor={maxMinor} currency={currency} />
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-[var(--text-secondary)]">
            {t('transfer.amountLabel')}
          </label>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            error={!!raw && !isValid}
          />
          {!!raw && amountMinor > maxMinor && (
            <p className="text-[11px] text-[var(--danger)]">
              Max: <Money minor={maxMinor} currency={currency} />
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </Button>
          <Button
            fullWidth
            disabled={!isValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '…' : t('actions.transfer')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Convert Cashback ──────────────────────────────────────────────────────────

interface ConvertCashbackModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The CONVERTIBLE part of the cashback pool, not the whole balance.
   *
   * The caller subtracts principal that still owes turnover, because
   * svc-wallet refuses an amount above it with `WALLET-PA-007`. Offering the
   * full pool here would put a number in front of the player that the server
   * has already decided to reject.
   */
  maxMinor: bigint;
  /** Principal held back, so the modal can say why the cap is lower than the tile. */
  lockedMinor?: bigint;
  /**
   * Wagering still owed — what the player has to DO, as opposed to
   * `lockedMinor`, which is what is being held. Kept separate because a
   * multiplier above 1 makes them different numbers.
   */
  turnoverRemainingMinor?: bigint;
  currency?: string;
}

export function ConvertCashbackModal({
  open,
  onClose,
  maxMinor,
  lockedMinor = 0n,
  turnoverRemainingMinor = 0n,
  currency,
}: ConvertCashbackModalProps) {
  const { t } = useTranslation('wallet');
  const formatMoney = useFormatMoney();
  const qc = useQueryClient();
  const [raw, setRaw] = useState('');
  const idempotencyKeyFor = useIdempotencyKey();

  // Currency-aware, not `* 100`: these move real balance between pools, so on a
  // 0-decimal brand the hardcoded multiplier transferred 100x what was typed
  // (same defect as the deposit/withdraw forms, WEB-02).
  // Parsed, not thrown: this runs in a render body, and `type="number"` accepts
  // `1e3` as a valid value, so `BigInt()` threw straight out of the component
  // and took the whole SPA with it (WEB-22). Unparseable text is simply not a
  // valid amount.
  const typedMinor = useTypedLedgerMinor(raw, currency);
  const amountMinor = typedMinor ?? 0n;
  const isValid = typedMinor !== null && amountMinor > 0n && amountMinor <= maxMinor;

  const mutation = useMutation({
    mutationFn: () =>
      convertCashback(
        Number(amountMinor),
        idempotencyKeyFor('convert-cashback', String(amountMinor)),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_QUERY_KEYS.balances });
      toast.success(t('actions.success.converted'));
      setRaw('');
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 501) {
        toast.info(t('actions.comingSoon'));
      } else {
        toast.error(mapApiError(err, t));
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{t('convert.modalTitle')}</DialogTitle>
        </DialogHeader>
        <div className="text-[12px] text-[var(--text-muted)]">
          {t('pools.cashback.sub')} ·{' '}
          <span className="text-[var(--info)] font-semibold">
            <Money minor={maxMinor} currency={currency} />
          </span>
        </div>
        {lockedMinor > 0n && (
          <p data-testid="convert-cashback-locked-note" className="text-[11px] text-[var(--warning)]">
            {t('cashbackLocked.modalNote', {
              locked: formatMoney(lockedMinor, currency),
              turnoverRemaining: formatMoney(turnoverRemainingMinor, currency),
            })}
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          <label className="text-[12px] font-semibold text-[var(--text-secondary)]">
            {t('convert.amountLabel')}
          </label>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            error={!!raw && !isValid}
          />
          {!!raw && amountMinor > maxMinor && (
            <p className="text-[11px] text-[var(--danger)]">
              Max: <Money minor={maxMinor} currency={currency} />
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" fullWidth onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel', { ns: 'common', defaultValue: 'Cancel' })}
          </Button>
          <Button
            fullWidth
            disabled={!isValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? '…' : t('actions.convert')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
